/**
 * useShellImports - what a session file's import does to the live table.
 *
 * - ROSTER (v4): the file carries the roster itself, so it REPLACES the
 *   current one rather than merging - two files both holding "Brother
 *   Castiel" are one person to a reader and two to an id comparison. The
 *   turn order is REPLACED too (v8.77.0), from the file's sheets.
 * - LOADOUTS: each saved through the library, as if saved by hand.
 * - CHARACTERS: a character file's people join the roster; nothing is replaced.
 * - BATTLEFIELD: each half independent, since each is its own checkbox - a
 *   GM importing somebody's map library rarely wants their fight moved on
 *   top of the one in progress. Tokens go through the path a drag uses, so
 *   the rev clock and the echo veto behave as they always do.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useCallback } from 'react';
import { emptyCombatState } from '../engine/roster/factories';

/** The file's combatants as they will stand: state and log kept or cleared. */
export function importedRoster(combatants, { keepState, keepNarrative }) {
  return combatants.map((c) => ({
    ...c,
    state: keepState === false ? emptyCombatState(c.sheet) : c.state,
    narrativeLog: keepNarrative ? c.narrativeLog : [],
  }));
}

/** Who the imported roster points at: the file's target if it is there, and the first character as actor. */
export function importedPointers(next, fileActive) {
  const firstId = next[0].id;
  const character = next.find((c) => c.kind === 'character');
  return {
    target: next.some((c) => c.id === fileActive) ? fileActive : firstId,
    actor: character ? character.id : firstId,
  };
}

/** A roster import: replace the roster, re-aim both pointers, replace the order. */
function useImportRoster({ setRoster, setActiveTab, setActorId, turnTracker }) {
  return useCallback(
    (combatants, { keepState, keepNarrative, activeTab: fileActive } = {}) => {
      if (!combatants?.length) return;
      const next = importedRoster(combatants, { keepState, keepNarrative });
      setRoster(next);
      const pointers = importedPointers(next, fileActive);
      setActiveTab(pointers.target);
      setActorId(pointers.actor);
      if (turnTracker.trackerState.active) {
        turnTracker.replaceParticipants(next.map((c) => ({ combatantId: c.id, name: c.name, sheet: c.sheet })));
      }
    },
    [setRoster, setActiveTab, setActorId, turnTracker],
  );
}

/**
 * A CHARACTER file (v8.414.0): its people JOIN the roster, after whoever
 * is already there. Nothing is replaced and no pointer moves, unlike a
 * session file - bringing somebody back must not disturb the table.
 */
function useImportCharacters({ setRoster, turnTracker }) {
  return useCallback(
    (combatants) => {
      if (!combatants?.length) return;
      setRoster((prev) => [...prev, ...combatants.map((c, i) => ({ ...c, order: prev.length + i }))]);
      if (turnTracker.trackerState.active) {
        turnTracker.addTabParticipantBatch(
          combatants.map((c) => ({ combatantId: c.id, name: c.name, sheet: c.sheet })),
        );
      }
    },
    [setRoster, turnTracker],
  );
}

/**
 * @param {object} args - setRoster, setActiveTab, setActorId, turnTracker,
 *   saveLoadout, loadMapWithRules, importMaps, writeToken.
 */
export default function useShellImports(args) {
  const { saveLoadout, loadMapWithRules, importMaps, writeToken } = args;
  const handleImportRoster = useImportRoster(args);
  const handleImportLoadouts = useCallback(
    (imported) => {
      for (const l of imported) saveLoadout(l.name, l.weapons);
    },
    [saveLoadout],
  );
  const handleImportBattlefield = useCallback(
    ({ terrain: nextTerrain, library: nextLibrary, tokens: nextTokens }) => {
      if (nextTerrain) loadMapWithRules(nextTerrain);
      if (nextLibrary?.length) importMaps(nextLibrary);
      if (nextTokens) for (const token of Object.values(nextTokens)) writeToken(token, { immediate: true });
    },
    [loadMapWithRules, importMaps, writeToken],
  );
  const handleImportCharacters = useImportCharacters(args);
  return { handleImportRoster, handleImportLoadouts, handleImportBattlefield, handleImportCharacters };
}
